import React from 'react';
import { Helmet } from 'react-helmet-async';
import MeditationTimer from '../components/MeditationTimer';
import Background from '../components/Background';
import { useFullscreen } from '../contexts/FullscreenContext';
import { getCanonicalUrl } from '../utils/seoUtils';

export default function MeditationPage() {
  const { isFullscreen } = useFullscreen();

  return (
    <div className="min-h-screen relative overflow-hidden">
      <Helmet>
        <title>Meditation Timer Online - Free Mindfulness Tool | Clock Online</title>
        <meta name="description" content="Free online meditation timer with bells, ambient sounds, and progress tracking. Enhance your mindfulness practice with customizable meditation sessions." />
        <link rel="canonical" href={getCanonicalUrl('/meditation')} />
        <meta property="og:title" content="Meditation Timer Online - Free Mindfulness Tool | Clock Online" />
        <meta property="og:description" content="Free online meditation timer with bells, ambient sounds, and progress tracking. Enhance your mindfulness practice with customizable meditation sessions." />
        <meta property="og:url" content={getCanonicalUrl('/meditation')} />
        <meta name="twitter:title" content="Meditation Timer Online - Free Mindfulness Tool | Clock Online" />
        <meta name="twitter:description" content="Free online meditation timer with bells, ambient sounds, and progress tracking." />
      </Helmet>
      <Background />
      
      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        {!isFullscreen && (
          <div className="text-center py-8">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg">
              Meditation Timer Online - Free Mindfulness Tool
            </h1>
            <p className="text-xl text-white/80 drop-shadow-md">
              Find inner peace through mindful meditation
            </p>
          </div>
        )}

        {/* Timer Container */}
        <div className="flex-1 flex items-start justify-center px-4 pb-8">
          <div className="w-full max-w-4xl">
            <MeditationTimer />
          </div>
        </div>

        {/* Description Section */}
        {!isFullscreen && (
          <div className="mt-16 max-w-4xl mx-auto px-6 text-white pb-16">
            <h2 className="text-3xl font-bold text-center mb-8 text-blue-300">
              Complete Guide to Meditation Timer Online
            </h2>
            
            <div className="space-y-8 text-lg leading-relaxed">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-2xl font-semibold mb-4 text-cyan-500">▶ How to Use Meditation Timer Online</h3>
                <p className="mb-4">
                  Our Meditation Timer Online is a powerful mindfulness tool designed to support your meditation practice. Simply set your desired meditation duration using the intuitive interface. Choose from quick preset durations like 5, 10, 15, 20, or 30 minutes, or set a custom duration that fits your schedule. Click "Start Meditation" to begin your session, and the Meditation Timer Online will guide you through your practice with gentle notifications.
                </p>
                <p className="mb-4">
                  The Meditation Timer Online provides a peaceful and distraction-free environment for your meditation practice. Whether you're a beginner exploring mindfulness or an experienced practitioner deepening your practice, the Meditation Timer Online offers the flexibility and features you need to create meaningful meditation sessions.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-2xl font-semibold mb-4 text-cyan-500">▶ Advanced Meditation Features</h3>
                <p className="mb-4">
                  The Meditation Timer Online includes advanced features to enhance your meditation experience. Customize your session with optional start and end bells that gently signal the beginning and end of your practice. Enable interval bells to help maintain focus during longer sessions. Choose from a variety of ambient sounds like nature sounds, gentle chimes, or peaceful melodies to create the perfect atmosphere for your meditation.
                </p>
                <p className="mb-4">
                  Personalize your Meditation Timer Online experience with background images including serene nature scenes, ocean waves, mountain sunsets, or zen gardens. Track your meditation progress with session statistics that record your total sessions, meditation minutes, and current streak. The Meditation Timer Online automatically saves your preferences, making it easy to maintain a consistent meditation routine.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-2xl font-semibold mb-4 text-cyan-500"><span className="text-yellow-400">💡</span> Important Usage Tips</h3>
                <p className="mb-4">
                  For the Meditation Timer Online to work properly, keep your browser tab active and do not close the web browser. Do not shut down your personal computer. Turn off Power Save mode on your device to ensure uninterrupted meditation sessions. The Meditation Timer Online requires an active browser tab to function, so ensure it remains open during your practice.
                </p>
                <p className="mb-4">
                  Create an optimal meditation environment by finding a quiet, comfortable space free from distractions. Sit with your back straight but relaxed, and gently close your eyes. Focus naturally on your breathing, allowing thoughts to pass without judgment. The Meditation Timer Online provides a peaceful digital space to support your mindfulness journey, whether you're practicing for 5 minutes or an hour.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-2xl font-semibold mb-4 text-cyan-500">▶ Benefits of Regular Meditation Practice</h3>
                <p className="mb-4">
                  Regular use of the Meditation Timer Online can help reduce stress, improve focus and concentration, enhance emotional well-being, and promote better sleep quality. Studies have shown that consistent meditation practice can lower blood pressure, reduce anxiety, and increase overall life satisfaction. The Meditation Timer Online makes it easy to establish and maintain a regular meditation routine.
                </p>
                <p className="mb-4">
                  Whether you practice mindfulness meditation, guided meditation, or silent meditation, the Meditation Timer Online adapts to your needs. Use the timer to structure your practice sessions, track your progress, and build a consistent meditation habit. The Meditation Timer Online supports both beginners who are just starting their mindfulness journey and experienced practitioners looking to deepen their practice.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-2xl font-semibold mb-4 text-cyan-500">▶ Customize Your Meditation Experience</h3>
                <p className="mb-4">
                  The Meditation Timer Online offers extensive customization options to create the perfect meditation environment for you. Choose from various ambient sound options including nature sounds, gentle meditation music, or peaceful chimes. Select background images that promote relaxation and focus. Adjust bell volumes and intervals to suit your preferences. The Meditation Timer Online remembers your settings, making it easy to return to your preferred meditation configuration.
                </p>
                <p className="mb-4">
                  Build your meditation practice with the Meditation Timer Online's progress tracking features. Monitor your meditation streak, total sessions completed, and cumulative meditation time. These statistics help you stay motivated and maintain consistency in your practice. The Meditation Timer Online is designed to support your long-term meditation journey, providing the tools and environment you need to develop a meaningful and sustainable meditation practice.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}